import type { AssistantMessage } from '@triedge/ai-assistant-ui';
import type {
  AdmissionsAIContextualAction,
  AdmissionsAssistantMessageMetadata,
  AdmissionsAssistantTaskThreadRollup,
  AdmissionsAssistantTaskThreadRollupEvent,
  AdmissionsCitation,
} from './types';

type AdmissionsAssistantConversationMessage = AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>;

const toMetadata = (
  message: AdmissionsAssistantConversationMessage,
): AdmissionsAssistantMessageMetadata | null => {
  const metadata = message.metadata;
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null;
  }
  return metadata as AdmissionsAssistantMessageMetadata;
};

const toRollupEvent = (
  message: AdmissionsAssistantConversationMessage,
  metadata: AdmissionsAssistantMessageMetadata,
): AdmissionsAssistantTaskThreadRollupEvent | null => {
  const taskThread = metadata.taskThread;
  if (!taskThread) {
    return null;
  }
  const taskProposal = metadata.taskProposal;
  return {
    messageId: message.messageId,
    timestamp: typeof message.timestamp === 'number' ? message.timestamp : null,
    label: taskProposal ? 'Task proposal' : taskThread.stateLabel,
    state: taskThread.state,
    summary: taskProposal?.summary ?? taskThread.statusMessage ?? null,
    content: message.content,
    artifactCount: taskThread.artifactCount ?? null,
  };
};

const resolveThreadVisibilityPriority = (
  metadata: AdmissionsAssistantMessageMetadata,
): number => {
  const taskThread = metadata.taskThread;
  if (!taskThread) {
    return 0;
  }
	  const hasArtifactPreview = Boolean(
	    metadata.artifactPreview?.workbookArtifact
	    || metadata.artifactPreview?.chartArtifact
	    || (metadata.artifactPreview?.chartPanels?.length ?? 0) > 0
	    || metadata.artifactPreview?.visualization3DArtifact
	    || metadata.artifactPreview?.visualization3DSpace
	  );
  if (hasArtifactPreview) {
    return 3;
  }
  if (
    taskThread.state === 'completed'
    || taskThread.state === 'failed'
    || taskThread.state === 'cancelled'
    || taskThread.state === 'hydrating_artifact'
  ) {
    return 2;
  }
  return metadata.taskProposal ? 0 : 1;
};

const isChartTaskThreadMetadata = (
  metadata: AdmissionsAssistantMessageMetadata | null | undefined,
): boolean =>
  String(metadata?.taskThread?.workflowKey ?? '').trim() ===
  'artifact.chart.generate';

const shouldPreferLaterVisibleThreadMessage = ({
  existingMetadata,
  nextMetadata,
  priority,
}: {
  existingMetadata: AdmissionsAssistantMessageMetadata | null | undefined;
  nextMetadata: AdmissionsAssistantMessageMetadata;
  priority: number;
}): boolean => {
  if (
    priority <= 1 &&
    isChartTaskThreadMetadata(existingMetadata) &&
    isChartTaskThreadMetadata(nextMetadata)
  ) {
    return false;
  }
  return true;
};

const resolveMetadataAtIndex = (
  messages: readonly AdmissionsAssistantConversationMessage[],
  metadataByMessageIndex: Map<number, AdmissionsAssistantMessageMetadata>,
  index: number | undefined,
): AdmissionsAssistantMessageMetadata | null => {
  if (index == null) {
    return null;
  }
  const existing = metadataByMessageIndex.get(index);
  if (existing) {
    return existing;
  }
  const message = messages[index];
  return message ? toMetadata(message) : null;
};

export const collapseAssistantTaskThreadReplies = (
  messages: readonly AdmissionsAssistantConversationMessage[],
): AdmissionsAssistantConversationMessage[] => {
  const visibleAssistantMessageIndexByThread = new Map<string, number>();
  const visibleAssistantMessagePriorityByThread = new Map<string, number>();
  const metadataByMessageIndex = new Map<number, AdmissionsAssistantMessageMetadata>();
  messages.forEach((message, index) => {
    if (message.role !== 'assistant') {
      return;
    }
    const metadata = toMetadata(message);
    const threadId = metadata?.taskThread?.threadId;
    if (!threadId) {
      return;
    }
    metadataByMessageIndex.set(index, metadata);
    const priority = resolveThreadVisibilityPriority(metadata);
    const existingPriority = visibleAssistantMessagePriorityByThread.get(threadId);
    const existingIndex = visibleAssistantMessageIndexByThread.get(threadId);
    if (
      existingPriority == null
      || priority > existingPriority
      || (
        priority === existingPriority &&
        (
          existingIndex == null ||
          (
            index > existingIndex &&
            shouldPreferLaterVisibleThreadMessage({
              existingMetadata: resolveMetadataAtIndex(
                messages,
                metadataByMessageIndex,
                existingIndex,
              ),
              nextMetadata: metadata,
              priority,
            })
          )
        )
      )
    ) {
      visibleAssistantMessagePriorityByThread.set(threadId, priority);
      visibleAssistantMessageIndexByThread.set(threadId, index);
    }
  });

  const hiddenEventsByThread = new Map<string, AdmissionsAssistantTaskThreadRollupEvent[]>();
  messages.forEach((message, index) => {
    if (message.role !== 'assistant') {
      return;
    }
    const metadata = metadataByMessageIndex.get(index) ?? toMetadata(message);
    const threadId = metadata?.taskThread?.threadId;
    if (!metadata || !threadId) {
      return;
    }
    const visibleIndex = visibleAssistantMessageIndexByThread.get(threadId);
    if (visibleIndex !== index) {
      const event = toRollupEvent(message, metadata);
      if (event) {
        const existing = hiddenEventsByThread.get(threadId) ?? [];
        existing.push(event);
        hiddenEventsByThread.set(threadId, existing);
      }
    }
  });

  const collapsedMessages: AdmissionsAssistantConversationMessage[] = [];
  messages.forEach((message, index) => {
    if (message.role !== 'assistant') {
      collapsedMessages.push(message);
      return;
    }

    const metadata = metadataByMessageIndex.get(index) ?? toMetadata(message);
    const threadId = metadata?.taskThread?.threadId;
    if (!metadata || !threadId) {
      collapsedMessages.push(message);
      return;
    }

    const visibleIndex = visibleAssistantMessageIndexByThread.get(threadId);
    if (visibleIndex !== index) {
      return;
    }

    const hiddenEvents = hiddenEventsByThread.get(threadId) ?? [];
    if (hiddenEvents.length === 0) {
      collapsedMessages.push(message);
      return;
    }

    const taskThreadRollup: AdmissionsAssistantTaskThreadRollup = {
      hiddenAssistantMessageCount: hiddenEvents.length,
      events: hiddenEvents,
    };

    collapsedMessages.push({
      ...message,
      metadata: {
        ...metadata,
        taskThreadRollup,
      } satisfies AdmissionsAssistantMessageMetadata,
    });
  });

  return collapsedMessages;
};
